import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Users, Shield, PenLine, Eye, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: "reader", label: "Reader", icon: Eye },
  { value: "writer", label: "Writer", icon: PenLine },
  { value: "editor", label: "Editor", icon: Shield },
  { value: "admin", label: "Admin", icon: Shield },
];

const AdminUsers = () => {
  const { getAuthHeaders } = useAuth();
  const { currentProject } = useProject();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState("");
  const [updatingId, setUpdatingId] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let params = [];
      if (currentProject) params.push(`project_id=${currentProject.id}`);
      if (filterRole) params.push(`role=${filterRole}`);
      
      const queryString = params.length > 0 ? `?${params.join("&")}` : "";
      const response = await axios.get(`${API}/users${queryString}`, { headers: getAuthHeaders() });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, filterRole]);

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingId(userId);
    try {
      await axios.put(
        `${API}/users/${userId}/role?role=${newRole}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("User role updated");
      await fetchUsers();
    } catch (error) {
      toast.error("Failed to update role");
    } finally {
      setUpdatingId(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "bg-purple-100 text-purple-800",
      editor: "bg-blue-100 text-blue-800",
      writer: "bg-green-100 text-green-800",
      reader: "bg-gray-100 text-gray-800"
    };
    return (
      <Badge className={styles[role] || styles.reader}>
        {role}
      </Badge>
    );
  };

  return (
    <AdminLayout title="User Management">
      <div className="space-y-6" data-testid="admin-users">
        {/* Filters */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#64748B]">
            <Users className="h-4 w-4" />
            <span>{users.length} users</span>
          </div>
          <Select value={filterRole || "all"} onValueChange={(v) => setFilterRole(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="filter-role-select">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {roles.map((r) => (
                <SelectItem key={r.value} value={r.value}>
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Current Role</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Change Role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5} className="h-16 animate-pulse bg-[#F8FAFC]" />
                  </TableRow>
                ))
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#3B82F6] flex items-center justify-center text-white font-semibold">
                          {user.name?.charAt(0) || "U"}
                        </div>
                        <span className="font-medium text-[#0F172A]">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-[#64748B]">{user.email}</TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell className="text-[#64748B]">
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Select
                        value={user.role}
                        onValueChange={(value) => handleRoleChange(user.id, value)}
                        disabled={updatingId === user.id}
                      >
                        <SelectTrigger className="w-[120px]" data-testid={`role-select-${user.id}`}>
                          {updatingId === user.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <SelectValue />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          {roles.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center">
                    <Users className="h-8 w-8 mx-auto mb-2 text-[#64748B] opacity-50" />
                    <p className="text-[#64748B]">No users found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
